import useGroupAPIStore from 'apis/admin/group.api';
import useRoleAPIStore from 'apis/admin/role.api';
import useAuthAPIStore from 'apis/auth/auth.api';
import useAPIStore from 'apis/common/common.api';
import { useDialogContext } from 'components/dialog/controller/DialogProvider';
import { Icon } from 'components/icon/Icon';
import { NavMenuProps, NavSubMenuProps } from 'consts/const';
import { CONSTANTS } from 'consts/getter';
import { UserModal } from 'containers/Admin/Users/Modal/UserModal';
import { LogoutDialog } from 'containers/Common/Dialog/LogoutDialog';
import { SwitchPagesDialog } from 'containers/Common/Dialog/SwitchPagesDialog';
import { Icons } from 'contents/icons';
import { useAuth } from 'hooks/Auth/useAuth';
import i18n from 'i18n';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import useStepsStore from 'stores/project/data/steps/useStepsStore';
import useDashboardStore from 'stores/project/data/useDashboardStore';
import useTableDataStore from 'stores/project/data/useTableDataStore';
import useReportDataStore from 'stores/report/useReportDataStore';
import useAccessStore from 'stores/useAccessStore';
import useDarkModeStore from 'stores/useDarkModeStore';
import useLayoutStore from 'stores/useLayoutStore';
import { useLocaleStore } from 'stores/useLocaleStore';
import useModalStore from 'stores/useModalStore';
import usePageStore from 'stores/usePageStore';
import useProjectDataStore from 'stores/useProjectDataStore';
import useToastStore from 'stores/useToastStore';
import { GroupNameType, RoleType } from 'types/admin/AdminType';
import { DropDownListOptionType } from 'types/common/CommonType';
import { Language } from 'types/language';
import { Util } from 'utils';
import { onKeyPressListener } from 'utils/keyboard';
import { getTranslation } from 'utils/translation';
import { FEATURES } from '../../../consts/feature/const';
import SubTab from '../SubTab/SubTab';
import { StyledNavigationBar } from './style';

const NavigationBar = () => {
  const { user, isLoggedIn, updateToken, logout } = useAuth();
  const { languages, locale, isResourceTestMode } = useLocaleStore();
  const { changeLanguage } = useAPIStore();
  const { setEditMode } = useModalStore();
  const { isDarkMode, setIsDarkMode } = useDarkModeStore();
  const { connectDatasetMode, projectData, isProjectInfoDashboard } = useProjectDataStore();
  const { selectedReportInfo } = useReportDataStore();
  const { getGroupNameList } = useGroupAPIStore();
  const { getRolesList } = useRoleAPIStore();
  const { gridSizeData } = useDashboardStore();
  const { isStepEditMode } = useStepsStore();
  const { isPage } = usePageStore();
  const { isFullScreen, setIsFullScreen } = useTableDataStore();
  const { activeToast } = useToastStore();
  const { openDialog } = useDialogContext();
  const { userLogout } = useAuthAPIStore();
  const { isSubTab } = useLayoutStore();
  const accessRole = useAccessStore((state) => state.accessRole);
  const navigate = useNavigate();

  const languageRef = useRef<HTMLDivElement>(null);
  const [isLanguagesVisible, setIsLanguagesVisible] = useState(false);
  const [userModal, setUserModal] = useState(false);
  const [isMenuList, setIsMenuList] = useState(false);
  const menuListRef = useRef<HTMLDivElement>(null);
  const locationPath = useLocation().pathname.split('/')[1];

  const [groupDropdownOption, setGroupDropdownOption] = useState<DropDownListOptionType[]>([]);
  const [roleDropdownOption, setRoleDropdownOption] = useState<DropDownListOptionType[]>([]);

  /**
   * @description role에 따른 서브 메뉴 필터링 항목
   * @author jh_jeong
   */
  const subMenuAccessMap: Record<string, Record<string, boolean>> = {
    admin: {
      users: accessRole.users.viewUsers,
      groups: accessRole.groups.viewGroupDetails,
      roles: accessRole.roles.viewRoles,
      domains: accessRole.administration.domainSettings,
      userLogs: accessRole.administration.viewUserLogs,
      connectors: accessRole.connectors.readConnector,
    },
    library: {
      logs: accessRole.library.viewExportLog,
    },
    report: {
      drafts: true,
      publish: accessRole.report.viewPublishedReport,
    },
  };

  // connector만 true이고 나머지가 모두 false인지 확인
  const shouldHideAdmin = Object.entries(subMenuAccessMap.admin)
    .filter(([key]) => key !== 'connectors') // connector 제외
    .every(([_, value]) => value === false); // 나머지가 모두 false인지 확인

  /**
   * @description role에 따른 메인 메뉴 필터링 항목
   * @author jh_jeong
   */
  const mainMenuAccessMap: Record<string, boolean> = {
    flow: accessRole.projectFlows.viewFlows,
    admin: !shouldHideAdmin,
  };

  const filteredMainMenu: NavMenuProps[] = (
    FEATURES.S_CORE_MODE ? CONSTANTS.S_CORE_NAV_MENU : CONSTANTS.NAV_MENU
  )
    .map((menu) => {
      const filteredChildren =
        menu.children?.filter((child) => {
          const pageSpecificAccess = subMenuAccessMap[menu.key] || {};
          return pageSpecificAccess[child.key] ?? true; // 서브 메뉴 접근 권한
        }) || [];

      return {
        ...menu,
        children: filteredChildren,
      };
    })
    .filter((menu) => {
      return mainMenuAccessMap[menu.key] ?? true; // 메인 메뉴 접근 권한
    });

  const activeSubMenu: NavSubMenuProps[] = (
    filteredMainMenu.find((menu) => menu.key === isPage)?.children ?? []
  ).filter((menu) => {
    return menu.isSuperUser ? user?.isSuperuser : true;
  });

  const dropdownOptions: {
    group: DropDownListOptionType[];
    role: DropDownListOptionType[];
  } = {
    group: groupDropdownOption,
    role: roleDropdownOption,
  };

  const handleLogout = () => {
    openDialog(LogoutDialog, {
      buttonClick: async () => {
        try {
          const data = await userLogout();
          if (data.code === 200) {
            logout();
          } else {
            throw new Error('Logout failed');
          }
        } catch (error) {
          console.error('An error occurred during logout:', error);
          // 에러 토스트 메시지 표시
          activeToast({
            message: getTranslation('message', 'ERROR.ERROR_OCCURRED'),
            type: 'error',
            timeout: 5000,
          });
        }
      },
    });
  };

  const linkOnClickHandler = (event: any, path: string) => {
    // 프로젝트 수정 후 저장하지 않고 다른 페이지로 이동할 경우 경고창 띄워주기
    if (locationPath === 'project' && projectData.projectId && isStepEditMode) {
      event.preventDefault();
      openDialog(SwitchPagesDialog, {
        buttonClick: () => {
          window.localStorage.removeItem('isRefresh');
          navigate(path);
        },
      });
    }
  };

  const handleFullScreenToggle = (value: boolean) => {
    setIsFullScreen(value);

    if (value) {
      activeToast({
        message: getTranslation('message', 'ACTION.FULL_SCREEN'),
        type: 'info',
        timeout: 3000,
      });
    }
  };

  const handleModalToggle = async (state: boolean) => {
    const fetchGroupList = async () => {
      const { resultList }: { resultList: GroupNameType[]; code: number } =
        await getGroupNameList(false);

      if (resultList?.length > 0) {
        setGroupDropdownOption(
          resultList.map((group: GroupNameType) => {
            return {
              label: group.groupName,
              value: group.groupId,
            };
          })
        );
      } else {
        // TODO: 그룹네임데이터가 없을 경우
      }
    };

    const fetchRoleList = async () => {
      const rolesList = await getRolesList('', '');

      setRoleDropdownOption(
        rolesList
          ? rolesList.map((roles: RoleType) => {
              return {
                label: roles.rolesName,
                value: roles.rolesId,
              };
            })
          : []
      );
    };
    await fetchGroupList();
    await fetchRoleList();
    setUserModal(state);
    setEditMode(state);
  };

  const handleLanguage = async (selectedLanguage: Language) => {
    if (!isLoggedIn) {
      i18n.changeLanguage(selectedLanguage);
      return;
    }

    if (user) {
      const { userId } = user;
      if (selectedLanguage !== locale) {
        await changeLanguage({ userId, languageCode: selectedLanguage }).then((response) => {
          const { access_TOKEN: accessToken } = response.resultObject;
          updateToken(accessToken);
        });
      }
      setIsLanguagesVisible(false);
    }
  };

  useEffect(() => {
    Util.handleObjectClose(menuListRef, setIsMenuList);
    onKeyPressListener('Escape', () => setIsFullScreen(false));
    const isDarkModeString = localStorage.getItem('dark-mode');
    if (isDarkModeString) setIsDarkMode(isDarkModeString === 'true');
  }, []);

  const handleMyinfoClick = () => {
    /** TODO: 페이지 이동 시 새로고침 발생 => Link로 변경 필요 - c_bae */
    // window.location.href = "/";
    handleModalToggle(true);
  };

  return (
    <StyledNavigationBar $isFullScreen={isFullScreen ? 'active' : ''}>
      <div className="header-container">
        <div className="navi-wrap">
          <div className="navi">
            {filteredMainMenu.map(
              (menu: any, idx: number) =>
                (menu.key === '' || isLoggedIn) && (
                  <div className={`${locationPath === menu.key && 'active'} tabs`} key={menu.key}>
                    <Link
                      key={idx}
                      to={(menu.children?.length && menu.children[0].path) || menu.path}
                      onClick={(event) => linkOnClickHandler(event, menu.path)}
                      className="menu"
                    >
                      {menu.icon}
                      {menu.title && <p>{menu.title}</p>}
                    </Link>
                  </div>
                )
            )}
          </div>
        </div>
        {FEATURES.I18N_DEV_MODE && isResourceTestMode && (
          <div
            style={{
              display: 'flex',
              flexFlow: 'row nowrap',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            <p style={{ fontSize: '18px', color: 'yellow', fontWeight: '900' }}>참고:</p>
            <p
              style={{
                fontSize: '12px',
                lineHeight: '18px',
                fontWeight: 600,
              }}
            >
              1. 앞에 $$- 가 붙은 것만 KEY이며, 그렇지 않은것은 KEY가 적용이 안된 부분입니다.
              <br />
              2. 번역본이 없는 경우 {`"ko-KR 번역없음 {KEY_NAME}"`} 과 같이 표시됩니다.
              <br />
              3. 키 이름 내에 . 이 들어가 있으면 MESSAGE KEY 입니다.
            </p>
          </div>
        )}
        <div className="right-box">
          {isProjectInfoDashboard && (
            <div className="project-info-wrap">
              {projectData.projectName && <p className="title">{projectData.projectName}</p>}
              {!connectDatasetMode && (
                <div className="info">
                  <div className="columns">
                    <p>
                      {getTranslation('resource', 'COLUMNS')}{' '}
                      <span>{gridSizeData.columns.toLocaleString()}</span>
                    </p>
                  </div>
                  <div className="rows">
                    <p>
                      {getTranslation('resource', 'ROWS')}{' '}
                      <span>{gridSizeData.rows.toLocaleString()}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          {selectedReportInfo && (
            <div className="project-info-wrap">
              <p className="title">{selectedReportInfo.reportName}</p>
              <div className="info">
                <div className="columns">
                  <p>
                    {getTranslation('resource', 'UPDATED')}{' '}
                    <span>{Util.formatDate(selectedReportInfo.lastModifiedDate, true)}</span>
                  </p>
                </div>
              </div>
            </div>
          )}
          {isLoggedIn && user?.isSuperuser && (
            <div className="super-user-flag">
              <Icons.Award className="super-user-icon" />
              <div>{getTranslation('resource', 'SUPER_USER')}</div>
            </div>
          )}

          {isLoggedIn && (
            <div className="profile-wrap">
              <div
                className={`profile ${user?.isSuperuser ? 'super-user' : ''}`}
                onClick={() => setIsMenuList(!isMenuList)}
                ref={menuListRef}
              >
                <p>{(user && user.loginId[0]) || ''}</p>
                <CSSTransition in={isMenuList} timeout={200} classNames={'combo-box'} unmountOnExit>
                  <ul className="menu-list">
                    <li className="list" onClick={() => handleLogout()}>
                      <div className="title">
                        <Icon icon="LogOut04" size={16} stroke="--text-gray-7" />
                        <p>{getTranslation('resource', 'LOGOUT')}</p>
                      </div>
                    </li>
                    <span className="rectangle" />

                    <li className="list" onClick={handleMyinfoClick}>
                      <div className="title">
                        <Icon icon="UserCircle" size={16} stroke="--text-gray-7" />
                        <p>{getTranslation('resource', 'MY_INFO')}</p>
                      </div>
                    </li>
                    {locationPath === 'project' && projectData.projectId && (
                      <li className="list" onClick={() => handleFullScreenToggle(!isFullScreen)}>
                        <div className="title">
                          <Icon icon="Maximize01" size={16} stroke="--text-gray-7" />
                          <p>{getTranslation('resource', 'FULL_SCREEN')}</p>
                        </div>
                      </li>
                    )}

                    {FEATURES.I18N_DEV_MODE && (
                      <li className="list" onClick={(e) => e.stopPropagation()}>
                        <div className="title">
                          <Icon icon="Globe02" size={16} stroke="--text-gray-7" />
                          <p>{getTranslation('resource', 'LANGUAGE')}</p>
                        </div>
                        <Icon icon="ChevronRight" size={16} stroke="--text-gray-7" />

                        <ul className="sub-menu-list" onClick={(e) => e.stopPropagation()}>
                          {languages.map((item) => (
                            <li
                              key={`item-${item.languageCode}`}
                              className={`list ${item.languageCode === locale ? 'selected' : ''}`}
                              onClick={() => handleLanguage(item.languageCode)}
                            >
                              <p>{item.displayName}</p>
                              {item.languageCode === locale && (
                                <Icon icon="Check" size={16} stroke="--text-gray-7" />
                              )}
                            </li>
                          ))}
                        </ul>
                      </li>
                    )}

                    <li
                      className="theme list"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        localStorage.setItem('dark-mode', String(!isDarkMode));
                        setIsDarkMode(!isDarkMode);
                      }}
                    >
                      <p>
                        {isDarkMode
                          ? getTranslation('resource', 'THEME_DARK')
                          : getTranslation('resource', 'THEME_LIGHT')}
                      </p>
                      <div className={`theme-btn ${isDarkMode ? 'dark' : 'light'}`}>
                        <span
                          className={`background`}
                          style={isDarkMode ? { left: 'calc(100% - 3rem)' } : { left: '0.2rem' }}
                        />
                        <div className={`sun icon ${!isDarkMode && 'active'}`}>
                          <Icon icon="Sun" size={16} stroke="--accent-mandarin" />
                        </div>
                        <div className={`moon icon ${isDarkMode && 'active'}`}>
                          <Icon icon="Moon02" size={16} stroke="--normal" />
                        </div>
                      </div>
                    </li>
                  </ul>
                </CSSTransition>
              </div>
            </div>
          )}
        </div>
      </div>
      {isSubTab && <SubTab tabMenu={activeSubMenu} />}
      <CSSTransition in={userModal} timeout={200} unmountOnExit classNames="modal">
        {/* 본인 계정은 권한 상관없이 수정할 수 있어야 함 */}
        <UserModal
          canModifyUsersRole={true}
          modalToggle={handleModalToggle}
          dropdownOptions={dropdownOptions}
          userId={user?.userId}
        />
      </CSSTransition>
    </StyledNavigationBar>
  );
};

export default NavigationBar;
